// Zähler und Zählerstand im Immobilienbereich – reine Funktionen (Felder wie ZaehlerFormular /
// AblesungFormular im Zählerbereich, Plausibilität wie dort; Zahlen mit deutschem Komma).

import { leseZahl } from './mietverhaeltnisFormular';

// Typen kommen aus der einen Typ-Konfiguration des Zählerbereichs
export { ZAEHLER_TYPEN } from '../zaehler/typen';

export interface ZaehlerEntwurf {
  zaehler_nummer: string; typ: string; eigenerTyp: string; einheit: string; standort: string;
  haus_id: string; einheit_bezeichnung: string; mieter_id: string; notizen: string;
}

export function zaehlerFehler(z: ZaehlerEntwurf): string | null {
  if (!z.zaehler_nummer.trim()) return 'Bitte eine Zählernummer angeben.';
  if (!(z.typ === '_eigen' ? z.eigenerTyp.trim() : z.typ)) return 'Bitte den Zählertyp angeben.';
  return null;
}

/** Payload wie bisher; Haus nur, wenn es zum Objekt gehört. */
export function zaehlerPayload(z: ZaehlerEntwurf, objektId: string, hausIds: readonly string[], jetzt: string): Record<string, string | null> {
  const t = (s: string) => s.trim() || null;
  return {
    zaehler_nummer: z.zaehler_nummer.trim(),
    typ: z.typ === '_eigen' ? z.eigenerTyp.trim() : z.typ,
    einheit: t(z.einheit),
    standort: t(z.standort),
    objekt_id: objektId,
    haus_id: z.haus_id && hausIds.includes(z.haus_id) ? z.haus_id : null,
    einheit_bezeichnung: t(z.einheit_bezeichnung),
    mieter_id: z.mieter_id || null,
    notizen: t(z.notizen),
    updated_at: jetzt,
  };
}

export interface Ablesung { wert: number; datum: string }

/** Ø-Verbrauch pro 30 Tage aus den Ablesungen (wie bisher: erste bis letzte). */
export function verbrauchProMonat(ablesungen: readonly Ablesung[]): number | null {
  if (ablesungen.length < 2) return null;
  const s = [...ablesungen].sort((a, b) => a.datum.localeCompare(b.datum));
  const erste = s[0]!; const letzte = s[s.length - 1]!;
  const tage = Math.max(1, (Date.parse(letzte.datum) - Date.parse(erste.datum)) / 86_400_000);
  const proMonat = ((letzte.wert - erste.wert) / tage) * 30;
  return proMonat > 0 ? proMonat : null;
}

export interface Pruefung { wert: number | null; vorher: Ablesung | null; verbrauch: number | null; warnung: string | null }

/**
 * Plausibilität wie bisher (rückwärts, mehr als doppelt oder weniger als ein Fünftel des Ø) –
 * aber gegen die letzte Ablesung VOR dem gewählten Datum, damit Nachträge richtig rechnen.
 */
export function pruefeAblesung(eingabe: string, datum: string, ablesungen: readonly Ablesung[], einheit: string): Pruefung {
  const wert = leseZahl(eingabe);
  if (wert === null) return { wert: null, vorher: null, verbrauch: null, warnung: null };
  const vorher = [...ablesungen].filter(a => a.datum <= datum).sort((a, b) => b.datum.localeCompare(a.datum))[0] ?? null;
  if (!vorher) return { wert, vorher: null, verbrauch: null, warnung: null };
  const verbrauch = Math.round((wert - vorher.wert) * 1000) / 1000;
  const f = (n: number) => n.toLocaleString('de-DE');
  if (verbrauch < 0) return { wert, vorher, verbrauch, warnung: `Zählerstand ist niedriger als der letzte Wert (${f(vorher.wert)} ${einheit}). Zähler rückwärts?` };
  const avg = verbrauchProMonat(ablesungen);
  if (avg) {
    const tage = Math.max(1, (Date.parse(datum) - Date.parse(vorher.datum)) / 86_400_000);
    const erwartet = (avg / 30) * tage;
    const text = (art: string) => `Verbrauch weicht stark ab (${f(verbrauch)} ${einheit} vs. Ø ${erwartet.toFixed(0)} ${einheit} für ${Math.round(tage)} Tage). Ungewöhnlich ${art}.`;
    if (verbrauch > erwartet * 2) return { wert, vorher, verbrauch, warnung: text('hoch') };
    if (verbrauch > 0 && verbrauch < erwartet * 0.2) return { wert, vorher, verbrauch, warnung: text('niedrig') };
  }
  return { wert, vorher, verbrauch, warnung: null };
}
